import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Typography,
  Box,
  Modal,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Skeleton,
  FormControlLabel,
  Checkbox,
  IconButton,
} from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import { ReactComponent as CloseIcon } from "../../../../vendor/_new/icon/navbar/notification/close_big.svg";

import colors from "../../../../utils/colors";
import { outlinedButtonSx, containedButtonSx } from "../../../common/Button";
import { tableHeadSx, tableBodySx } from "../detection/WAFEvent";
import { formatDate } from "../../../../utils/format";
import { getStatusLabel } from "../sites";
import { getEnforcementLabel } from "../sites";

import useSite from "../../../../hooks/user/useSite";

const AddSiteModal = ({ open, sites, handleClose, getProjectList, projectId }) => {
  const navigate = useNavigate();

  const { moveSite } = useSite();

  const [selectedSites, setSelectedSites] = useState([]);
  const [isMoving, setIsMoving] = useState(false);

  const handleAddSite = async () => {
    setIsMoving(true);
    await moveSite(projectId, { site_id: selectedSites });
    await getProjectList();
    setIsMoving(false);
    handleClose();
  };

  useEffect(() => {
    setSelectedSites([]);
    setIsMoving(false);
  }, [open]);

  return (
    <Modal 
      open={open} 
      onClose={handleClose}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
        }
      }}
    >
      <Stack
        spacing={4}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "75%",
          backgroundColor: colors.neutral[100],
          borderRadius: "8px",
          p: 6,
        }}
      >
        <Stack spacing={3} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
          <Typography sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}>
            Add Site
          </Typography>
          <IconButton onClick={handleClose} aria-label="Close" sx={{ p: 0 }}>
            <CloseIcon sx={{ color: colors.neutral[600] }} />
          </IconButton>
        </Stack>
        <Box
          sx={{
            py: 4,
            pl: 4,
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
          }}
        >
          <ReactPerfectScrollbar style={{ maxHeight: `calc(70vh - 158px)` }}>
            <Box sx={{ height: "100%", pr: 4 }}>
              <Table
                sx={{
                  "& .MuiTableCell-head": { py: 3, px: 3 },
                  "& .MuiTableCell-body": { py: 2, px: 3 },
                  "& .MuiTableCell-root": { borderBottom: "none" },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "20.4%" }}>
                      <Typography sx={tableHeadSx}>Domain</Typography>
                    </TableCell>
                    <TableCell sx={{ width: "10.2%" }}>
                      <Typography sx={tableHeadSx}>Origin</Typography>
                    </TableCell>
                    <TableCell sx={{ width: "16.2%" }}>
                      <Typography sx={tableHeadSx}>On boarded</Typography>
                    </TableCell>
                    <TableCell sx={{ width: "11.1%" }}>
                      <Typography sx={tableHeadSx}>Status</Typography>
                    </TableCell>
                    <TableCell sx={{ width: "11.1%" }}>
                      <Typography sx={tableHeadSx}>Enforcement</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sites?.map((site) => (
                    <TableRow key={`site_${site.id}`}>
                      <TableCell
                        sx={{
                          width: "20.4%",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedSites?.includes(site?.site_id)}
                              onChange={() => {
                                if (selectedSites?.includes(site?.site_id)) {
                                  setSelectedSites((prev) => prev?.filter((e) => e !== site?.site_id));
                                } else {
                                  setSelectedSites((prev) => [...prev, site?.site_id]);
                                }
                              }}
                              size="small"
                              sx={{
                                color: colors.neutral[300], // border / unchecked color
                                "&.Mui-checked": {
                                  color: colors.secondary[300], // checkmark & filled color
                                },
                              }}
                            />
                          }
                          label={site.site_id}
                          sx={{
                            height: 21,
                            "& .MuiFormControlLabel-label": {
                              fontSize: 14,
                              lineHeight: "21px",
                              fontWeight: "medium",
                              "&:hover": { color: colors.primary[300] },
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: "10.2%" }}>
                        <Typography sx={tableBodySx}>{site.addr}</Typography>
                      </TableCell>
                      <TableCell sx={{ width: "16.2%" }}>
                        <Typography sx={tableBodySx}>{formatDate(site?.created_date) || "—"}</Typography>
                      </TableCell>
                      <TableCell sx={{ width: "11.1%" }}>{getStatusLabel(!site?.enabled)}</TableCell>
                      <TableCell sx={{ width: "11.1%" }}>
                        {getEnforcementLabel(site.status) ?? (
                          <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </ReactPerfectScrollbar>
        </Box>
        <Stack direction={"row"} alignItems={"center"} justifyContent={"flex-end"} spacing={4}>
          <Button onClick={() => navigate("/v2/sites/new")} sx={{ ...outlinedButtonSx, width: 200, height: 44 }}>
            Create new site
          </Button>
          <Button
            disabled={selectedSites?.length === 0 || isMoving}
            onClick={handleAddSite}
            sx={{ ...containedButtonSx, width: 200, height: 44 }}
          >
            Add site
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};
export default AddSiteModal;
